import type {
  Bbox,
  Device,
  ErrorEnvelope,
  OsmToilet,
  SearchResult,
  UserToilet,
  Visit,
} from "./types";

export class ApiError extends Error {
  constructor(
    public status: number,
    public envelope: ErrorEnvelope,
  ) {
    super(envelope.message);
  }

  get errorCode(): string {
    return this.envelope.error_code;
  }
}

export interface ApiContext {
  /** Returns the current device id (UUID). */
  getDeviceId(): string;
  /** Whether the device has been registered server-side (captcha cleared). */
  isRegistered(): boolean;
  /** Mark device as registered after a successful POST /api/devices. */
  markRegistered(): void;
  /** Display name to send to POST /api/devices on first-write captcha. */
  getDisplayName(): string;
  /** Icon to send to POST /api/devices on first-write captcha. */
  getIcon(): string;
  /** Trigger the captcha flow and resolve to a Turnstile token (or reject on cancel). */
  requireCaptchaToken(): Promise<string>;
}

const JSON_HEADERS = { "Content-Type": "application/json" } as const;

function bboxParam(bbox: Bbox): string {
  return bbox.join(",");
}

async function readEnvelope(res: Response): Promise<ErrorEnvelope> {
  try {
    const data: unknown = await res.json();
    if (typeof data === "object" && data !== null && "error_code" in data) {
      return data as ErrorEnvelope;
    }
  } catch {
    /* fall through */
  }
  return { error_code: "INTERNAL", message: `HTTP ${res.status}` };
}

export class ApiClient {
  constructor(
    private ctx: ApiContext,
    public baseUrl: string = "",
  ) {}

  private headers(extra?: Record<string, string>): Record<string, string> {
    return { ...JSON_HEADERS, "X-Device-Id": this.ctx.getDeviceId(), ...(extra ?? {}) };
  }

  private async request<T>(
    method: string,
    path: string,
    init?: { body?: unknown; query?: Record<string, string | number | undefined> },
    isWrite: boolean = false,
  ): Promise<T> {
    const url = this.buildUrl(path, init?.query);
    const opts: RequestInit = {
      method,
      headers: this.headers(),
      ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    };
    let res = await fetch(url, opts);

    // Auto-recover from "device not registered yet" by running the captcha flow once.
    if (
      isWrite &&
      (res.status === 403 || res.status === 404) &&
      !this.ctx.isRegistered()
    ) {
      const env = await readEnvelope(res.clone());
      if (env.error_code === "CAPTCHA_FAILED" || env.error_code === "DEVICE_NOT_REGISTERED") {
        await this.registerWithCaptcha();
        res = await fetch(url, opts);
      }
    }

    if (!res.ok) {
      const env = await readEnvelope(res);
      throw new ApiError(res.status, env);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  private buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
    const base = `${this.baseUrl}${path}`;
    if (!query) return base;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== "") qs.set(k, String(v));
    }
    const s = qs.toString();
    return s ? `${base}?${s}` : base;
  }

  async registerWithCaptcha(): Promise<Device> {
    const token = await this.ctx.requireCaptchaToken();
    const body = {
      device_id: this.ctx.getDeviceId(),
      display_name: this.ctx.getDisplayName(),
      icon: this.ctx.getIcon(),
      turnstile_token: token,
    };
    const res = await fetch(`${this.baseUrl}/api/devices`, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const env = await readEnvelope(res);
      throw new ApiError(res.status, env);
    }
    this.ctx.markRegistered();
    return (await res.json()) as Device;
  }

  patchMe(payload: { display_name?: string; icon?: string }): Promise<Device> {
    return this.request<Device>("PATCH", "/api/devices/me", { body: payload }, true);
  }

  getOsmToilets(bbox: Bbox, signal?: AbortSignal): Promise<OsmToilet[]> {
    return this.requestRaw<OsmToilet[]>("GET", "/api/osm/toilets", {
      query: { bbox: bboxParam(bbox) },
      signal,
    });
  }

  getUserToilets(bbox: Bbox, signal?: AbortSignal): Promise<UserToilet[]> {
    // Always opt in to Personal pins. The server filters by X-Device-Id, so
    // other devices' Personal pins are never returned to us regardless of this
    // flag. This avoids depending on the in-memory `registered` bit, which is
    // lost on every page reload (so Personal pins kept disappearing after F5).
    return this.requestRaw<UserToilet[]>("GET", "/api/toilets", {
      query: { bbox: bboxParam(bbox), include_personal: 1 },
      signal,
    });
  }

  createToilet(payload: {
    lat: number;
    lon: number;
    is_public: boolean;
    notes?: string;
  }): Promise<UserToilet> {
    return this.request<UserToilet>("POST", "/api/toilets", { body: payload }, true);
  }

  search(q: string, signal?: AbortSignal): Promise<SearchResult[]> {
    return this.requestRaw<SearchResult[]>("GET", "/api/search", { query: { q }, signal });
  }

  getVisits(): Promise<Visit[]> {
    return this.request<Visit[]>("GET", "/api/visits");
  }

  markVisit(
    toilet_ref: string,
    source: "osm-live" | "osm-snapshot" | "user",
    visit_type: 1 | 2 = 1,
  ): Promise<Visit> {
    return this.request<Visit>(
      "POST",
      "/api/visits",
      { body: { toilet_ref, source, visit_type } },
      true,
    );
  }

  deleteVisit(id: number): Promise<void> {
    return this.request<void>("DELETE", `/api/visits/${id}`, undefined, true);
  }

  // Variant of request that supports an AbortSignal for read endpoints.
  private async requestRaw<T>(
    method: string,
    path: string,
    init?: {
      body?: unknown;
      query?: Record<string, string | number | undefined>;
      signal?: AbortSignal;
    },
  ): Promise<T> {
    const url = this.buildUrl(path, init?.query);
    const opts: RequestInit = {
      method,
      headers: this.headers(),
      ...(init?.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
      ...(init?.signal ? { signal: init.signal } : {}),
    };
    const res = await fetch(url, opts);
    if (!res.ok) {
      const env = await readEnvelope(res);
      throw new ApiError(res.status, env);
    }
    return (await res.json()) as T;
  }
}

export function friendlyMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      const after = err.envelope.retry_after_seconds;
      return after
        ? `you're going too fast - try again in ${after} seconds`
        : "you're going too fast - try again later";
    }
    if (err.status === 503) {
      return "service temporarily unavailable - try again shortly";
    }
    if (err.errorCode === "INVALID_NOTES") {
      return "your note is too long or contains invalid characters";
    }
    if (err.errorCode === "BBOX_TOO_LARGE") {
      return "zoom in a bit to load toilets";
    }
    if (err.errorCode === "BBOX_ANTIMERIDIAN") {
      return "this map area isn't supported - try a different region";
    }
    if (err.errorCode === "DEVICE_NOT_REGISTERED") {
      return "complete the quick verification to start contributing";
    }
    if (err.errorCode === "CAPTCHA_FAILED") {
      return "verification failed - please try again";
    }
    if (err.errorCode === "NOT_FOUND") {
      return "that toilet isn't available";
    }
    return err.envelope.message;
  }
  if (err instanceof TypeError) {
    return "network error - check your connection";
  }
  return "something went wrong";
}
